import React, { useState } from 'react';
import { User, Mail, Lock, Save, AlertTriangle } from 'lucide-react';
import apiClient from '../utils/apiClient';

// Este componente recibe el 'user' logueado
const Perfil = ({ user }) => {
  // Estados para los campos del formulario
  // Inicializamos con los datos del usuario
  const [nombre, setNombre] = useState(user?.nombre || '');
  const [email, setEmail] = useState(user?.email || '');
  const [nickname, setNickname] = useState(user?.nickname || '');
  
  // Estados para el cambio de contraseña
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // H-NEW-2: ruta plural `/usuarios/<id>` (singular era 404).
      // H-NEW-3 ya parchado en backend para aceptar PUT parcial.
      await apiClient.put(`/usuarios/${user.id}`, { nombre, email, nickname });
      setSuccess('Perfil actualizado correctamente.');
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al actualizar el perfil.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      setError('Debes ingresar tu contraseña actual.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las nuevas contraseñas no coinciden.');
      return;
    }
    // Alinear con backend (auth.py:cambiar_password):
    // ≥8 caracteres + al menos una mayúscula + al menos un dígito.
    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('La nueva contraseña debe tener al menos una letra mayúscula.');
      return;
    }
    if (!/\d/.test(newPassword)) {
      setError('La nueva contraseña debe tener al menos un número.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // H-NEW-7: enviar current_password (backend lo valida con bcrypt).
      await apiClient.put(`/usuario/${user.id}/cambiar_password`, {
        password: newPassword,
        current_password: currentPassword,
      });
      setSuccess('¡Contraseña actualizada con éxito!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.error) || 'Error al cambiar la contraseña.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="p-8 bg-gray-900 min-h-screen text-gray-100">
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Mi Perfil</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Formulario de Datos Personales */}
        <div className="bg-gray-800 rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-blue-400 mb-6">Datos Personales</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="nombre">Nombre Completo</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text" id="nombre" value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="email">Correo Electrónico</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="email" id="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
             <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="nickname">Nickname (Usuario)</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text" id="nickname" value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-800 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-300 flex items-center justify-center disabled:opacity-50"
            >
              <Save size={20} className="mr-2" />
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </form>
        </div>

        {/* Formulario de Cambio de Contraseña */}
        <div className="bg-gray-800 rounded-2xl shadow-xl p-6">
          <h2 className="text-2xl font-bold text-blue-400 mb-6">Cambiar Contraseña</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="current-pass">Contraseña Actual</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="password" id="current-pass" value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="new-pass">Nueva Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="password" id="new-pass" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="confirm-pass">Confirmar Nueva Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="password" id="confirm-pass" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-700 text-gray-100 placeholder-gray-400 border-2 border-transparent focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-800 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-300 flex items-center justify-center disabled:opacity-50"
            >
              <Lock size={20} className="mr-2" />
              {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
            </button>
          </form>
        </div>
      </div>

      {/* Mensajes de Error/Éxito */}
      {error && (
        <div className="mt-6 bg-red-500 bg-opacity-20 text-red-300 p-3 rounded-lg text-center flex items-center justify-center">
          <AlertTriangle size={18} className="mr-2" />
          {error}
        </div>
      )}
      {success && (
        <div className="mt-6 bg-green-500 bg-opacity-20 text-green-300 p-3 rounded-lg text-center">
          {success}
        </div>
      )}

    </div>
  );
};

export default Perfil;
